import React from 'react';
import { ArrowRightIcon, BanknoteIcon, SendIcon, ShieldCheckIcon } from 'lucide-react';
import { LandingHeader } from '../components/LandingHeader';
import { GettingStarted } from '../components/landing/GettingStarted';
import { IntegrationsSection } from '../components/landing/IntegrationsSection';
import { InvoiceFlowPanel } from '../components/landing/InvoiceFlowPanel';
import { LatePaymentStats } from '../components/landing/LatePaymentStats';
import { SolutionsSection } from '../components/landing/SolutionsSection';
import { VideoSection } from '../components/landing/VideoSection';
const painPoints = [{
  icon: ShieldCheckIcon,
  title: 'Know who you are trusting',
  description: 'Monitor and manage your credit risk for existing and future customers, with ratings, suggested limits and alerts when circumstances change.'
}, {
  icon: SendIcon,
  title: 'Get paid 4.4x faster',
  description: 'Automate payment chasing with your own email templates and escalation sequences, so overdue invoices are pursued without anyone chasing manually.'
}, {
  icon: BanknoteIcon,
  title: 'Unlock cash when you need it',
  description: 'Draw on fast, flexible invoice finance against what you are already owed, rather than waiting on your customers’ payment runs.'
}];
export function LandingPage() {
  return <div id="top" className="min-h-screen w-full bg-white text-[#041F60]" data-id="element-680" data-mp="3i9extbc8htg4">
      <a href="#main-content" className="sr-only fixed left-4 top-4 z-50 rounded-lg bg-[#041F60] px-4 py-2 text-sm font-bold text-white focus:not-sr-only focus:outline-none focus:ring-2 focus:ring-[#4BE4FF]" data-id="element-681" data-mp="3i9587ov3poay">
        Skip to content
      </a>
      <LandingHeader data-id="element-682" data-mp="2qvft8kjke5" />

      <main id="main-content" data-id="element-683" data-mp="3a38h4i8seg">
        <section className="border-b border-[#A6B6F8]/50 bg-[#A6B6F8]/20" data-id="element-684" data-mp="1ww3h4rd0uyba">
          <div className="mx-auto grid max-w-6xl gap-12 px-5 py-14 sm:px-8 sm:py-20 lg:grid-cols-[1.18fr_0.82fr] lg:items-center lg:gap-12 lg:py-24" data-id="element-685" data-mp="1wxwrohqra2ok">
            <div data-id="element-686" data-mp="355ap55k7k8ur">
              <h1 className="text-[2.15rem] font-bold leading-[1.02] tracking-[-0.045em] text-[#041F60] sm:text-[3.2rem] lg:text-[3.7rem]" data-id="element-687" data-mp="356vk2wafptt5">
                Reduce bad debts
                <br data-id="element-688" data-mp="3e7a9a6qirt1x" />
                and{' '}
                <span className="text-[#5B5299] underline decoration-[#4BE4FF] decoration-[0.14em] underline-offset-[0.12em]" data-id="element-689" data-mp="3e8259io13ssq">
                  get paid quicker.
                </span>
              </h1>
              <p className="mt-6 max-w-lg text-base leading-7 text-[#3A3A3A] sm:text-lg" data-id="element-690" data-mp="3o33pq17d06oz">
                Our 3-in-1 solution takes the stress, time and complexity out of
                cash-flow management.
              </p>
              <div className="mt-8 flex flex-wrap gap-3" data-id="element-691" data-mp="3svn3oz9yl3ub">
                <a href="/signup" className="inline-flex items-center gap-2 rounded-lg bg-[#041F60] px-5 py-3.5 text-sm font-bold text-white transition hover:bg-[#031849] focus:outline-none focus:ring-2 focus:ring-[#041F60] focus:ring-offset-2 focus:ring-offset-[#A6B6F8]/20" data-id="element-692" data-mp="3sw6ocnqzu6c1">
                  Start your free trial{' '}
                  <ArrowRightIcon size={17} aria-hidden="true" data-id="element-693" data-mp="uaslb5sx6s4d" />
                </a>
                <a href="#walkthroughs" className="inline-flex items-center gap-2 rounded-lg border border-[#041F60] px-5 py-3.5 text-sm font-bold text-[#041F60] transition hover:bg-[#041F60] hover:text-white focus:outline-none focus:ring-2 focus:ring-[#041F60] focus:ring-offset-2 focus:ring-offset-[#A6B6F8]/20" data-id="element-694" data-mp="ff2wz109i2wh">
                  Find out more
                </a>
              </div>
            </div>
            <InvoiceFlowPanel data-id="element-695" data-mp="3srd025m59koi" />
          </div>
        </section>

        <VideoSection data-id="element-696" data-mp="1ny0u8w3rsqw" />

        <section id="benefits" className="scroll-mt-6 bg-white" data-id="element-697" data-mp="1nfzixul2oc4">
          <div className="mx-auto max-w-6xl px-5 py-16 sm:px-8 sm:py-24" data-id="element-698" data-mp="3jtuaqp1gwk6t">
            <div className="mx-auto max-w-2xl text-center" data-id="element-699" data-mp="3nuw9z5vdww8s">
              <h2 className="text-3xl font-bold tracking-[-0.035em] sm:text-4xl" data-id="element-700" data-mp="3nwh44udsed64">
                Remove the pain of poor cash flow.
              </h2>
              <p className="mt-5 text-base leading-7 text-[#3A3A3A] sm:text-lg" data-id="element-701" data-mp="3brtnkhvj2okv">
                Our 3-in-1 solution helps reduce the risk of bad debt and
                automate the process of getting you paid.
              </p>
            </div>

            <ul className="mt-12 grid gap-5 md:grid-cols-3" data-id="element-702" data-mp="3207eos6nx1la">
              {painPoints.map(point => <li key={point.title} className="flex h-full flex-col rounded-2xl border border-[#A6B6F8]/60 bg-[#A6B6F8]/10 p-6 text-center sm:p-7" data-id="element-703" data-mp="2xyc4szao1fvw">
                  <span className="mx-auto grid h-11 w-11 place-items-center rounded-xl bg-[#041F60] text-[#4BE4FF]" data-id="element-704" data-mp="2o4c02yjaqcnp">
                    <point.icon size={20} aria-hidden="true" data-id="element-705" data-mp="maw2tispdzjr" />
                  </span>
                  <h3 className="mt-5 text-lg font-bold tracking-tight text-[#041F60]" data-id="element-706" data-mp="hc1gw7zfvuvf">
                    {point.title}
                  </h3>
                  <p className="mt-2.5 text-sm leading-6 text-[#3A3A3A]" data-id="element-707" data-mp="psa85zrw96rc">
                    {point.description}
                  </p>
                </li>)}
            </ul>

            <div className="mt-10 text-center" data-id="element-708" data-mp="ymeuc5w31u0j">
              <a href="/signup" className="inline-flex items-center gap-2 rounded-lg bg-[#041F60] px-5 py-3.5 text-sm font-bold text-white transition hover:bg-[#031849] focus:outline-none focus:ring-2 focus:ring-[#041F60] focus:ring-offset-2" data-id="element-709" data-mp="ynpyi6tfx3s6">
                Get started <ArrowRightIcon size={17} aria-hidden="true" data-id="element-710" data-mp="axm48q256gdu" />
              </a>
            </div>
          </div>
        </section>

        <LatePaymentStats data-id="element-711" data-mp="kui8ukox6id4" />
        <SolutionsSection data-id="element-712" data-mp="kus0sdtiyzlg" />
        <GettingStarted data-id="element-713" data-mp="krvzqgmdruss" />
        <IntegrationsSection data-id="element-714" data-mp="1raazlyzpwu8x" />

        <section className="navy-texture border-t border-[#031849] text-white" data-id="element-715" data-mp="1r8zo1d016i79">
          <div className="mx-auto flex max-w-6xl flex-col items-start justify-between gap-8 px-5 py-14 sm:px-8 sm:py-20 md:flex-row md:items-center" data-id="element-716" data-mp="1w8m7i46gcuoi">
            <div className="max-w-xl" data-id="element-717" data-mp="16n31vcyog5w4">
              <h2 className="text-3xl font-bold tracking-[-0.035em] sm:text-4xl" data-id="element-718" data-mp="11mezxw8wu4n4">
                Get started now.
              </h2>
              <p className="mt-3 text-base leading-7 text-[#A6B6F8]" data-id="element-719" data-mp="1ghv26ojn96ul">
                Start your free trial and bring credit, collections and invoice
                finance into one practical view.
              </p>
            </div>
            <a href="/signup" className="inline-flex shrink-0 items-center gap-2 rounded-lg bg-[#4BE4FF] px-5 py-3.5 text-sm font-bold text-[#041F60] transition hover:bg-[#38d9f2] focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-[#041F60]" data-id="element-720" data-mp="ns5w3ws2ugm4">
              Start your free trial{' '}
              <ArrowRightIcon size={17} aria-hidden="true" data-id="element-721" data-mp="52pu150qug3l" />
            </a>
          </div>
        </section>
      </main>

      <footer className="bg-white" data-id="element-722" data-mp="4o2dvp958gi">
        <div className="mx-auto flex max-w-6xl flex-col gap-5 px-5 py-8 sm:flex-row sm:items-center sm:justify-between sm:px-8" data-id="element-723" data-mp="2bsjdwajxfmuw">
          <p className="text-sm font-semibold text-[#041F60]" data-id="element-724" data-mp="2gk1euyly9nfv">Credit Manager</p>
          <a href="mailto:support@creditmanager.co.uk" className="text-sm font-semibold text-[#5B5299] underline decoration-[#A6B6F8] underline-offset-4 transition hover:text-[#041F60] focus:text-[#041F60] focus:outline-none" data-id="element-725" data-mp="21ulqtqdszw3v">
            Contact support
          </a>
          <p className="text-xs text-stone-500" data-id="element-726" data-mp="2zi3z703jyw7c">© 2026 Credit Manager</p>
        </div>
      </footer>
    </div>;
}